/**
 * Frontend mirror of the backend `applyImportConfig` + `detectImportConfig`
 * pure transforms (proly-backend/src/lib/mapper/parse.ts).
 *
 * Algorithm is copied VERBATIM — do NOT diverge. Shared fixtures in
 * proly-frontend/tests/v2/fixtures/import-cases.json (mirrored to
 * proly-backend/test/v2/fixtures/import-cases.json) pin parity between
 * the two implementations. Any algorithm change MUST be made in BOTH files
 * simultaneously and must keep both parity test suites green.
 */

import type { ImportConfig, MergeRange, SourceCell, SourceRow } from "./types";

// ---------------------------------------------------------------------------
// Constants (must match backend)
// ---------------------------------------------------------------------------

/** Minimum fraction of cells that must be non-empty for a row to be a header candidate. */
const HEADER_FILL_THRESHOLD = 0.6;

/** If a row is below this fraction it is considered a "group label" row to skip. */
const GROUP_LABEL_THRESHOLD = 0.3;

// ---------------------------------------------------------------------------
// Private helpers (mirrors of backend private helpers)
// ---------------------------------------------------------------------------

/** Return true if a cell value counts as "non-empty". */
function isNonEmpty(cell: unknown): boolean {
  if (cell === null || cell === undefined) return false;
  if (typeof cell === "string") return cell.trim().length > 0;
  return true; // number, boolean, Date
}

/** Stringify and trim a cell to produce a header label. */
function cellToHeader(cell: unknown): string {
  if (cell === null || cell === undefined) return "";
  return String(cell).trim();
}

/** Convert a raw cell value to a SourceCell (null for empties, not ""). */
function cellToSourceCell(cell: unknown): SourceCell {
  if (cell === null || cell === undefined) return null;
  if (typeof cell === "string") {
    const trimmed = cell.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  if (typeof cell === "number" || typeof cell === "boolean") return cell;
  if (cell instanceof Date) return cell;
  // Fallback: stringify
  const s = String(cell).trim();
  return s.length > 0 ? s : null;
}

/**
 * Compute the effective column count across all rows.
 * Handles ragged rows (rows may have different lengths).
 */
function maxColCount(rows: unknown[][]): number {
  let max = 0;
  for (const row of rows) {
    if (row.length > max) max = row.length;
  }
  return max;
}

/**
 * Count non-empty cells in a row, normalising against `colCount` so sparse rows
 * at the end don't produce misleadingly high fill ratios.
 */
function fillRatio(row: unknown[], colCount: number): number {
  if (colCount === 0) return 0;
  const nonEmpty = row.filter(isNonEmpty).length;
  return nonEmpty / colCount;
}

/**
 * Make header labels unique and non-empty so they are safe to use as object keys.
 * Blank labels become "Column N" (1-based position); duplicates get " (n)" suffix.
 * Uniquification runs over ALL columns (including excluded ones) before any
 * exclusion is applied — stable names regardless of exclusions.
 */
function uniquifyHeaders(rawHeaders: string[]): string[] {
  const used = new Set<string>();
  return rawHeaders.map((label, i) => {
    let name = label.trim() || `Column ${i + 1}`;
    if (used.has(name)) {
      let n = 2;
      while (used.has(`${name} (${n})`)) n++;
      name = `${name} (${n})`;
    }
    used.add(name);
    return name;
  });
}

/**
 * Detect the header row index in a sheet's raw rows.
 *
 * Strategy (in order):
 *  1. Scan each row. If it has < GROUP_LABEL_THRESHOLD fill AND the next row
 *     has >= HEADER_FILL_THRESHOLD fill, the next row is the header (group-label
 *     skip).
 *  2. Otherwise: first row with >= HEADER_FILL_THRESHOLD fill where at least one
 *     row after it has any non-empty cell.
 *  3. Fall back to row 0.
 *
 * Returns the 0-based index of the header row, or 0 if no match.
 */
function detectHeaderRowIdx(rows: unknown[][], colCount: number): number {
  if (rows.length === 0) return 0;

  // Pass 1: group-label skip
  for (let i = 0; i < rows.length - 1; i++) {
    const ratio = fillRatio(rows[i] ?? [], colCount);
    const nextRatio = fillRatio(rows[i + 1] ?? [], colCount);
    if (ratio < GROUP_LABEL_THRESHOLD && nextRatio >= HEADER_FILL_THRESHOLD) {
      return i + 1;
    }
  }

  // Pass 2: density scan with "has later data" check
  for (let i = 0; i < rows.length; i++) {
    const ratio = fillRatio(rows[i] ?? [], colCount);
    if (ratio >= HEADER_FILL_THRESHOLD) {
      // Check that at least one row after i has a non-empty cell
      const hasLaterData = rows
        .slice(i + 1)
        .some((row) => (row ?? []).some(isNonEmpty));
      if (hasLaterData) return i;
    }
  }

  return 0;
}

// ---------------------------------------------------------------------------
// Public API (mirrors backend exports from parse.ts)
// ---------------------------------------------------------------------------

/**
 * Apply an ImportConfig to a raw array-of-arrays grid, producing the final
 * unique headers and the data rows (keyed by header). Pure and total.
 *
 * Mirror of backend `applyImportConfig` in proly-backend/src/lib/mapper/parse.ts.
 * Algorithm must remain byte-identical to the backend — verified by shared
 * fixtures in tests/v2/fixtures/import-cases.json.
 */
export function applyImportConfig(
  rawGrid: unknown[][],
  merges: MergeRange[],
  config: ImportConfig,
): { headers: string[]; rows: SourceRow[] } {
  const colCount = maxColCount(rawGrid);
  if (colCount === 0) return { headers: [], rows: [] };

  // 1. Forward-fill merges that intersect the header range.
  const filled: unknown[][] = rawGrid.map((row) => (row ? [...row] : []));
  for (const m of merges) {
    if (m.e.r < config.headerRowStart || m.s.r > config.headerRowEnd) continue;
    const topLeft = rawGrid[m.s.r]?.[m.s.c] ?? null;
    for (let r = m.s.r; r <= m.e.r; r++) {
      if (r < config.headerRowStart || r > config.headerRowEnd) continue;
      if (!filled[r]) filled[r] = [];
      for (let c = m.s.c; c <= m.e.c; c++) filled[r][c] = topLeft;
    }
  }

  // 2. Flatten header rows per column.
  const rawHeaders: string[] = [];
  for (let c = 0; c < colCount; c++) {
    const parts: string[] = [];
    for (let r = config.headerRowStart; r <= config.headerRowEnd; r++) {
      const label = cellToHeader(filled[r]?.[c] ?? null);
      if (label) parts.push(label);
    }
    rawHeaders[c] = parts.join(config.joinSeparator);
  }

  // 3. Overrides (rawColIdx wins).
  for (const [c, name] of Object.entries(config.headerOverrides)) {
    const idx = Number(c);
    if (Number.isInteger(idx) && idx >= 0 && idx < colCount) rawHeaders[idx] = name;
  }

  // 4. Uniquify across ALL columns (before exclusion).
  const uniqueAll = uniquifyHeaders(rawHeaders);

  // 5. Drop excluded columns.
  const excluded = new Set(config.excludedColIdxs);
  const keepCols: number[] = [];
  for (let c = 0; c < colCount; c++) if (!excluded.has(c)) keepCols.push(c);
  const headers = keepCols.map((c) => uniqueAll[c]);

  // 6. Emit data rows (skip explicit skips + all-empty rows).
  const skipped = new Set(config.skippedRowIdxs);
  const rows: SourceRow[] = [];
  for (let i = config.dataStartIdx; i < rawGrid.length; i++) {
    if (skipped.has(i)) continue;
    const rawRow = rawGrid[i] ?? [];
    if (!rawRow.some(isNonEmpty)) continue;
    const sourceRow: SourceRow = {};
    keepCols.forEach((c, idx) => {
      sourceRow[headers[idx]] = cellToSourceCell(rawRow[c] ?? null);
    });
    rows.push(sourceRow);
  }
  return { headers, rows };
}

/**
 * Auto-detect an ImportConfig from a raw grid + merge ranges.
 * Returns the suggested config and a confidence score (0..1).
 *
 * Mirror of backend `detectImportConfig` in proly-backend/src/lib/mapper/parse.ts.
 */
export function detectImportConfig(
  rawGrid: unknown[][],
  merges: MergeRange[],
): { config: ImportConfig; confidence: number } {
  const empty: ImportConfig = {
    headerRowStart: 0,
    headerRowEnd: 0,
    dataStartIdx: 1,
    skippedRowIdxs: [],
    excludedColIdxs: [],
    headerOverrides: {},
    joinSeparator: " / ",
  };
  const colCount = maxColCount(rawGrid);
  if (colCount === 0 || rawGrid.length === 0) return { config: empty, confidence: 0 };

  const headerRowIdx = detectHeaderRowIdx(rawGrid, colCount);
  let headerRowStart = headerRowIdx;
  let headerRowEnd = headerRowIdx;

  // Expand into a multi-row header when a merge intersects the detected row
  // (grouped header: the merged label row sits ABOVE the attribute row).
  // A horizontal merge (e.c > s.c) spanning the detected header row implies
  // a sub-header row immediately below (the attribute row).
  for (const m of merges) {
    if (m.s.r <= headerRowIdx && m.e.r >= headerRowIdx && m.e.c > m.s.c) {
      headerRowStart = Math.min(headerRowStart, m.s.r);
      // If the merge is purely horizontal (same row), expand end to include
      // the row immediately below (the attribute/sub-header row).
      const expandedEnd = m.e.r === m.s.r ? m.e.r + 1 : m.e.r;
      headerRowEnd = Math.max(headerRowEnd, expandedEnd);
    }
  }

  const config: ImportConfig = {
    ...empty,
    headerRowStart,
    headerRowEnd,
    dataStartIdx: headerRowEnd + 1,
  };
  const confidence = fillRatio(rawGrid[headerRowStart] ?? [], colCount);
  return { config, confidence };
}
